/**
 * Income attribution: every change of a player's gold and diamonds that the server stores names its source.
 *
 * All writes of a canonical game state go through ProfileService (commitCanonical: commands, battles, store, ads,
 * operators, the session entry, profile creation; save / import: the client-owned states of L0 / L1 and imports). Each
 * such write
 *  - takes the balance before and after from the states (resource-ledger.ts currenciesOf), and the EconomySource its
 *    caller gives (a command's op and opId, a battle's id and verdict, a purchase, an ad ticket, a ledger entry);
 *  - checks the stored balance against the ANCHOR of the last instrumented write (ProfileRecord.econ: the balance the
 *    attributed changes left, written in the same document): a difference is a change that no instrumented path made
 *    (a gap: storage edited by hand, a write path that bypassed the services);
 *  - stores the new anchor with the state (one document write, so a crash never leaves them apart);
 *  - tells the listeners (ProfileService.onEconomy: the anti-cheat) once the write is stored.
 * The reconciliation job of the anti-cheat compares the stored balance with the anchor too, so a gap is found also when
 * no later write follows. Nothing here refuses or changes anything: attribution only observes. A storage migration that
 * changes a balance must set the anchor of the document it changes (`anchorOf`), or the change counts as a gap.
 */
import type { PlayerId, ValidationVerdict } from '../../../shared/protocol/model';
import type { LedgerCurrencies } from './resource-ledger';

/** Where a stored change of the canonical state came from. */
export type EconomySourceKind =
  /** player.command (`op`, `ref` = opId); battle.finish adds the battle's id, verdict and replay. */
  | 'command'
  /** The session entry of profile.load (session.enter). */
  | 'session'
  /** A new initial state (profile.create): the starting capital (`dev`: the developer profile). */
  | 'start'
  /** A store purchase credited (`ref` = purchaseId). */
  | 'purchase'
  /** A refunded / revoked purchase taken back (`ref` = purchaseId). */
  | 'refund'
  /** A rewarded ad (`ref` = ticketId, `op` = placement). */
  | 'ad'
  /** An operator's change (`ref` = the resource ledger entry). */
  | 'admin'
  /** profile.save at L0 / L1: a state the client made (not verifiable). */
  | 'client_save'
  /** A local or legacy save imported into a fresh profile (profile.save { reason: 'import' }). */
  | 'import'
  /** A write whose caller named no source (a programming gap: counts as outside the known sources). */
  | 'unknown';

export interface EconomySource {
  kind: EconomySourceKind;
  /** The command op (kind 'command') or the ad placement. */
  op?: string;
  /** opId, purchaseId, ticketId or ledger entry id. */
  ref?: string | null;
  /** battle.finish: the battle, the server's verdict and the stored replay. */
  battleId?: string;
  verdict?: ValidationVerdict;
  replayId?: string;
  /** profile.create: the developer profile (config.devProfiles). */
  dev?: boolean;
}

/** ProfileRecord.econ: the balance of the canonical state as the last instrumented write left it. */
export interface EconAnchor {
  [field: string]: number;
  revision: number;
  gold: number;
  diam: number;
  /** Epoch ms of that write. */
  at: number;
}

/** A balance change that no instrumented write made, found before a write (or by the reconciliation job). */
export interface EconomyGap {
  /** The anchor's revision and time: the change happened after them. */
  sinceRevision: number;
  sinceAt: number;
  /** The anchor's balance and the stored one. */
  expected: LedgerCurrencies;
  found: LedgerCurrencies;
  /** found - expected. */
  delta: LedgerCurrencies;
  /** The revision of the stored state that carries it. */
  revision: number;
}

/** One stored change of a canonical state (ProfileService.onEconomy). */
export interface EconomyChange {
  playerId: PlayerId;
  at: number;
  /** The revision the change produced. */
  revision: number;
  /** null: no state (before: a new profile) or one the rules cannot read. */
  before: LedgerCurrencies | null;
  after: LedgerCurrencies | null;
  source: EconomySource;
  gap: EconomyGap | null;
}

export type EconomyListener = (change: EconomyChange) => void;

/** The gap between an anchor and the stored balance, or null when they agree (or either is unknown). */
export function gapOf(anchor: EconAnchor | undefined | null, found: LedgerCurrencies | null, revision: number): EconomyGap | null {
  if (!anchor || !found) return null;
  if (anchor.gold === found.gold && anchor.diam === found.diam) return null;
  return {
    sinceRevision: anchor.revision,
    sinceAt: anchor.at,
    expected: { gold: anchor.gold, diam: anchor.diam },
    found: { gold: found.gold, diam: found.diam },
    delta: { gold: found.gold - anchor.gold, diam: found.diam - anchor.diam },
    revision,
  };
}

/** The anchor of a stored balance (null when the state has none). */
export function anchorOf(c: LedgerCurrencies | null, revision: number, at: number): EconAnchor | null {
  return c ? { revision, gold: c.gold, diam: c.diam, at } : null;
}
